import { AuthProvider, UserRole } from '@avanzu/types';
import { Alert, List, Typography } from '@mui/material';
import React, { useState } from 'react';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import ContentWrapper from '../../layout/ContentWrapper';
import GoogleAccountLink from './providers/GoogleAccountLink';
import ResetPassword from './providers/ResetPassword';
import SetPassword from './providers/SetPassword';
import XeroAccountLink from './providers/XeroAccountLink';
import { useSnackbar } from 'notistack';

export const SecurityRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

interface OAuthError {
  error: string;
  provider: AuthProvider;
}

const Security: React.FC = () => {
  const [oauthError, setOauthError] = useState<OAuthError | null>(null);
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const clearError = () => {
    setOauthError(null);
  };

  if (!user) {
    return (
      <ContentWrapper breadcrumb>
        <PaperSection title="Connected Accounts">Loading...</PaperSection>
      </ContentWrapper>
    );
  }
  const handleOAuthError = (error: string, provider: AuthProvider) => {
    setOauthError({ error, provider });
  };

  const handleOAuthSuccess = (message: string, provider: AuthProvider) => {
    enqueueSnackbar(message, { variant: 'success' });
  };

  return (
    <ContentWrapper breadcrumb>
      <PaperSection title="Connected Accounts">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Link your accounts to enable single sign-on with the following trusted providers.
        </Typography>

        {oauthError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOauthError(null)}>
            {oauthError.error}
          </Alert>
        )}

        <GoogleAccountLink
          onError={handleOAuthError}
          onClearError={clearError}
          onSuccess={handleOAuthSuccess}
        />
        <List>
          <XeroAccountLink
            onError={handleOAuthError}
            onClearError={clearError}
            onSuccess={handleOAuthSuccess}
          />
        </List>
      </PaperSection>
      {user.providers && user.providers.includes(AuthProvider.PASSWORD) && <ResetPassword />}
      {user.providers && !user.providers.includes(AuthProvider.PASSWORD) && <SetPassword />}
    </ContentWrapper>
  );
};

export default Security;
